// Product Description Reveal Toggle - Improved Version
class ProductDescriptionReveal {
  constructor(container) {
    this.container = container;
    this.textElement = container.querySelector('[data-reveal-text]');
    this.toggle = container.querySelector('[data-reveal-toggle]');
    this.labelMore = container.querySelector('[data-reveal-label-more]');
    this.labelLess = container.querySelector('[data-reveal-label-less]');
    
    if (!this.textElement || !this.toggle) return;
    
    // Store the actual max-height in pixels for accurate comparison
    this.truncatedHeight = this.getTruncatedHeight();
    
    this.init();
  }
  
  /**
   * Get the truncated height from CSS (in pixels)
   * This is more reliable than parsing max-height
   */
  getTruncatedHeight() {
    const computedStyle = getComputedStyle(this.textElement);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const fontSize = parseFloat(computedStyle.fontSize);
    
    // If line-height is 'normal', estimate it as 1.6 * fontSize
    const actualLineHeight = isNaN(lineHeight) ? fontSize * 1.6 : lineHeight;
    
    // Desktop: 6 lines, Mobile: 5 lines
    const isMobile = window.innerWidth < 750;
    const numberOfLines = isMobile ? 5 : 6;
    
    return actualLineHeight * numberOfLines;
  }
  
  init() {
    // Initial check
    this.checkContentHeight();
    
    // Add click handler
    this.toggle.addEventListener('click', () => this.toggleReveal());
    
    // Recheck on window resize (debounced)
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.truncatedHeight = this.getTruncatedHeight();
        this.checkContentHeight();
      }, 150);
    });
  }
  
  /**
   * Check if content should be truncated
   * Uses improved logic with better threshold
   */
  checkContentHeight() {
    // Ensure we're in the collapsed state for accurate measurement
    const wasExpanded = this.container.classList.contains('is-expanded');
    this.container.classList.remove('is-expanded');
    
    // Force a reflow to ensure styles are applied
    void this.textElement.offsetHeight;
    
    // Get the full content height (what would be displayed if not truncated)
    const fullHeight = this.textElement.scrollHeight;
    
    // Get the truncated height threshold
    const truncatedHeight = this.truncatedHeight;
    
    // Calculate the difference
    const heightDifference = fullHeight - truncatedHeight;
    
    // Improved threshold logic:
    // - If difference is less than 1.5 lines, consider it "short" (no truncation needed)
    // - This prevents the "Read more" from appearing for 4-5 line descriptions
    const computedStyle = getComputedStyle(this.textElement);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const fontSize = parseFloat(computedStyle.fontSize);
    const actualLineHeight = isNaN(lineHeight) ? fontSize * 1.6 : lineHeight;
    
    // Threshold: Only show "Read more" if content exceeds by more than 1.5 lines
    const threshold = actualLineHeight * 1.5;
    
    // Debug logging (can be removed in production)
    console.log('Description reveal check:', {
      fullHeight: Math.round(fullHeight),
      truncatedHeight: Math.round(truncatedHeight),
      difference: Math.round(heightDifference),
      threshold: Math.round(threshold),
      lineHeight: Math.round(actualLineHeight),
      needsTruncation: heightDifference > threshold
    });
    
    // Determine if toggle should be shown
    if (heightDifference > threshold) {
      // Content is long enough - show toggle
      this.container.classList.remove('is-short');
    } else {
      // Content is short - hide toggle and show full content
      this.container.classList.add('is-short');
      this.container.classList.add('is-expanded'); // Show full content
    }
    
    // Restore expanded state if it was previously expanded
    if (wasExpanded && !this.container.classList.contains('is-short')) {
      this.container.classList.add('is-expanded');
    }
  }
  
  /**
   * Toggle between collapsed and expanded states
   * Includes smooth animation via CSS transitions
   */
  toggleReveal() {
    const isExpanded = this.container.classList.toggle('is-expanded');
    
    // Update aria-expanded attribute
    this.toggle.setAttribute('aria-expanded', isExpanded);
    
    // Toggle label visibility
    if (isExpanded) {
      // Expanding - show "Read less"
      this.labelMore.style.display = 'none';
      this.labelLess.style.display = 'inline';
    } else {
      // Collapsing - show "Read more"
      this.labelMore.style.display = 'inline';
      this.labelLess.style.display = 'none';
      
      // Scroll to the description with smooth behavior
      // Wait a bit for the collapse animation to start
      setTimeout(() => {
        this.container.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest' 
        });
      }, 50);
    }
  }
}

// Initialize all reveal containers
function initDescriptionReveal() {
  const revealContainers = document.querySelectorAll('[data-reveal-content]');
  revealContainers.forEach(container => {
    new ProductDescriptionReveal(container);
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDescriptionReveal);
} else {
  initDescriptionReveal();
}

// Re-initialize if the page uses AJAX navigation (like Shopify sections)
document.addEventListener('shopify:section:load', initDescriptionReveal);